import React from 'react';
import { Image, StyleSheet, TouchableOpacity, View, Text, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useGlobalState } from '../../shared/state';
import { Styles } from '../../styles';
import { greenDot, Icons, redDot, orangeDot } from '../../assets';
import SizeScale from '../../components/SizeScale/SizeScale';
import { I18N } from '../../shared/util';
import { getSizeScaleCategoriesAndMarker } from '../../shared/products';


export type ProductCardDTO = {
  id?: number
  name?: string
  brand?: string
  gtin?: string
  price?: number
  categoryName?: string
  salePrice?: any
  size: Size
  image_url?: string
  thumb_url?: string
  description?: string
  last_width_category?: number
  width_name?: string
  msi?: boolean
  leading_system?: string
  stock?: number
  mpn?: string
  insoleSizes?: any
  isInsole?: boolean
  projectedStock: number
};

export type Size = {
  eu?: number
  uk?: number
  us?: number
  secondary_eu?: number
  secondary_uk?: number
  secondary_us?: number
}

const ProductCard: React.FC<ProductCardDTO> = (product: ProductCardDTO): JSX.Element => {
  const [, setModalIsDisplayed] = useGlobalState('modalIsDisplayed');
  const [currentScanner] = useGlobalState('currentScanner');
  const [Colors] = useGlobalState('colors');
  const [selectedShoe, setSelectedShoe] = useGlobalState("selectedShoe");
  const [language] = useGlobalState('language');
  const i18n = I18N(language);
  const navigation = useNavigation();
  const isSteitz = currentScanner.partner && currentScanner.partner?.toLowerCase() === "steitz";
  const primarySize: any = product.leading_system === 'eu' ? product.size.eu : product.leading_system === 'uk' ? product.size.uk : product.size.us
  const secondarySize: any = currentScanner.enableSecondarySize || !('enableSecondarySize' in currentScanner)
    ? product.leading_system === 'eu'
      ? product.size.secondary_eu
      : product.leading_system === 'uk'
        ? product.size.secondary_uk
        : product.size.secondary_us
    : undefined
  const price: any = product.price || undefined
  const salePrice: any = product.salePrice || undefined

  const openProductDetailModal = (data: ProductCardDTO) => {
    setSelectedShoe(data)
    navigation.navigate(Platform.OS === 'web' ? 'ProductDetail' : 'ProductDetailModal', { product: data, isInsole: product.isInsole });
    setModalIsDisplayed(true);
  }

  const handleShowingStockColors = () => {
    if (product.stock && product.stock > 0) {
      return greenDot
    } else if (product.stock === 0 && product.projectedStock > 0) {
      return orangeDot
    } else if (product.stock === 0) {
      return redDot
    } else {
      return null
    }
  }

  const handleShowingStockText = () => {
    if (product.stock && product.stock > 0) {
      return `${i18n.t('In stock')} (${product.stock})`
    } else if (product.stock === 0 && product.projectedStock > 0) {
      return `${i18n.t('projectedStock')} (${product.projectedStock})`
    } else if (product.stock === 0) {
      return i18n.t('out of stock')
    } else {
      return null
    }
  }

//calculate postions of the size scale markers
const { categories, markerIndex } = getSizeScaleCategoriesAndMarker(primarySize, secondarySize);

return (
    <TouchableOpacity style={styles.touchContainer} activeOpacity={0.6} onPress={() => openProductDetailModal(product)}>
      <View style={Styles.card}>
        <View style={styles.productCard}>
          {product.msi && isSteitz && <Image source={Icons.customizeIcon} style={styles.customizeIcon} />}
          {/* Hide soleIcon and soleIconText if partner is Birkenstock */}
          {currentScanner.partner?.toLowerCase() !== "birkenstock" && (
            <>
              <Image source={Icons.soleIcon} style={styles.soleIcon} />
              <Text style={styles.soleIconText}>{product.last_width_category}</Text>
            </>
          )}
          <Image source={{ uri: product.thumb_url ? product.thumb_url : product.image_url }} style={styles.imageContainer} />
        </View>
        <View style={styles.productCardDetails}>
          {product.name != undefined && <Text adjustsFontSizeToFit style={styles.productCardNameText} >{product.name}</Text>}
          {product.width_name != undefined && <Text style={styles.widthNameText}>{product.width_name}</Text>}
          {product.price != undefined &&
            <View style={{ flexDirection: 'row' }}>
              <Text style={styles.productCardPriceText}>{`${i18n.t('price')}: `}</Text>
              {salePrice && price && salePrice < price ?
                <>
                  <Text style={[styles.productCardPriceText, { textDecorationLine: 'line-through' }]}>{`${price}€ `}</Text>
                  <Text style={[styles.productCardPriceText, { color: 'red' }]}>{`${salePrice}€`}</Text>
                </>
                : <Text style={styles.productCardPriceText}>{`${price}€`}</Text>
              }
            </View>}
          {[product.brand != undefined || product.gtin != undefined] && <Text style={styles.productCardManufacturerText}>{product.brand} - {product.gtin}</Text>}
          {currentScanner.displayStock &&
            <View style={styles.inStockWrapper}>
              <Image style={styles.dotWrapper} source={handleShowingStockColors()} />
              <Text style={styles.inStockText}>{handleShowingStockText()}</Text>
            </View>}
        </View>
        <View style={[styles.sizeContainer, { backgroundColor: Colors.mainColorDark.color }]}>
          <SizeScale
            caption={"EU"}
            valueCount={categories.length}
            categories={categories}
            values={{ left: markerIndex }}
          />
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default ProductCard;

const styles = StyleSheet.create({
  touchContainer: {
    height: '49%',
    width: '33%',
    backgroundColor: 'transparent',
    paddingTop: '0.5%',
    paddingLeft: '0.5%'
  },
  productCard: {
    height: '65%',
    backgroundColor: 'white'
  },
  soleIcon: {
    width: '7%',
    height: Platform.OS === 'web' ? '25%' : '75%',
    right: Platform.OS === 'web' ? 43 : 25,
    top: Platform.OS === 'web' ? '10%' : '-15%',
    position: 'absolute',
    resizeMode: 'contain',
    zIndex: 10
  },
  customizeIcon: {
    position: 'absolute',
    width: '15%',
    height: '20%',
    top: 10,
    zIndex: 2,
    left: 3,
    resizeMode: 'contain',
  },
  soleIconText: {
    fontFamily: 'Montserrat-SemiBold',
    fontSize: 12,
    position: 'absolute',
    right: '12%',
    top: '18%',
    zIndex: 11
  },
  imageContainer: {
    flex: 1,
    height: undefined,
    width: undefined,
    resizeMode: 'contain'
  },
  productCardDetails: {
    height: '35%',
    width: '72%',
    flexDirection: 'column',
    paddingLeft: '3%',
    paddingBottom: '3%'
  },
  productCardNameText: {
    flex: 1,
    flexWrap: 'wrap',
    fontFamily: 'Montserrat-Black',
  },
  productCardPriceText: {
    fontFamily: 'Montserrat-SemiBold',
    fontSize: 14,
    fontWeight: '100',
  },
  widthNameText: {
    fontFamily: 'Montserrat-Regular',
    fontSize: 10,
    fontWeight: '600',
    right: Platform.OS === 'web' ? "-26%" : "-33%",
    position: 'absolute'
  },
  productCardManufacturerText: {
    fontFamily: 'Montserrat-Black',
    fontSize: 10,
    fontWeight: '300',
  },
  sizeContainer: {
    flex: 1,
    flexDirection: 'row',
    position: 'absolute',
    height: '27%',
    width: '26%',
    right: 0,
    bottom: 0
  },
  sizeContainerText: {
    fontFamily: 'Montserrat-Light',
    fontSize: 17,
    color: 'white'
  },
  inStockWrapper: {
    flexDirection: 'row',
    width: 'auto',
    justifyContent: 'flex-start',
    left: -9
  },
  dotWrapper: {
    width: '15%',
    height: '70%',
    alignSelf: 'center',
    resizeMode: 'contain'
  },
  inStockText: {
    fontFamily: 'Montserrat-SemiBold',
    fontSize: 12,
    fontWeight: '100',
  }

});
